import { registerComponent } from "helpers";
import { EventDetailsCalendar } from "event-details-calendar";
import {
    bindEventModalActions,
    openAddToCalendarModal,
    openEventDetailsModal,
    openSharedEventFromUrl,
} from "event-details-modals";

const VIEW_QUERY_PARAM = "events-view";
const VALID_VIEWS = ["list", "calendar"];

registerComponent(
    "event-details-filter",
    class EventDetailsFilter extends HTMLElement {
        constructor() {
            super();
            this.initialized = false;
        }

        connectedCallback() {
            if (this.initialized) {
                return;
            }

            this.initialized = true;
            this.init();
        }

        init() {
            this.currentLocation = "all";
            this.currentView = "list";
            this.calendarRendered = false;

            this.filterButtons = this.querySelectorAll(
                ".event-details-section__header-filter-location-button[data-location]"
            );
            this.viewButtons = this.querySelectorAll(
                ".event-details-section__header-grid-control-button[data-view]"
            );
            this.listView = this.querySelector(".event-details-section__list-view");
            this.calendarView = this.querySelector(
                ".event-details-section__calendar-view"
            );
            this.calendarMount = this.querySelector("[data-calendar-mount]");
            this.listMonthGroups = this.querySelectorAll(
                ".event-details-section__list-view .event-details-section__month-group"
            );
            this.listEventItems = this.querySelectorAll(
                ".event-details-section__content-item"
            );

            this.events = this.parseEventsData();
            this.enelioCalendarEntries = this.parseEnelioCalendarData();

            this.handleFilterClick = this.handleFilterClick.bind(this);
            this.handleViewClick = this.handleViewClick.bind(this);
            this.handlePopState = this.handlePopState.bind(this);

            this.eventCalendar = this.createEventCalendar();

            this.filterButtons.forEach((button) => {
                button.addEventListener("click", this.handleFilterClick);
            });

            this.viewButtons.forEach((button) => {
                button.addEventListener("click", this.handleViewClick);
            });

            bindEventModalActions(this, this.events, {
                openDetails: openEventDetailsModal,
                openCalendar: openAddToCalendarModal,
            });

            window.addEventListener("popstate", this.handlePopState);

            this.ensureCalendarRendered();
            this.syncFromUrl();
            openSharedEventFromUrl(this.events);
        }

        createEventCalendar() {
            if (!this.calendarMount) {
                return null;
            }

            return new EventDetailsCalendar(
                this.calendarMount,
                this.events,
                this.enelioCalendarEntries
            );
        }

        isEventCalendarInstance(instance) {
            return Boolean(
                instance &&
                instance.root instanceof Element &&
                typeof instance.render === "function"
            );
        }

        getEventCalendar() {
            if (this.isEventCalendarInstance(this.eventCalendar)) {
                return this.eventCalendar;
            }

            this.eventCalendar = this.createEventCalendar();
            this.calendarRendered = false;
            return this.eventCalendar;
        }

        closeEventCalendarPopover() {
            const root = this.calendarMount;

            if (root) {
                root
                    .querySelectorAll(".event-details-calendar__popover")
                    .forEach((popover) => popover.remove());

                root
                    .querySelectorAll(".event-details-calendar__day.is-popover-open")
                    .forEach((day) => day.classList.remove("is-popover-open"));
            }

            const eventCalendar = this.eventCalendar;

            if (!eventCalendar) {
                return;
            }

            eventCalendar.activePopoverDay = null;

            if (typeof eventCalendar.unbindPopoverListeners === "function") {
                eventCalendar.unbindPopoverListeners();
            }
        }

        ensureCalendarRendered() {
            const eventCalendar = this.getEventCalendar();

            if (!eventCalendar || this.calendarRendered) {
                return;
            }

            eventCalendar.render();
            this.calendarRendered = true;
        }

        disconnectedCallback() {
            window.removeEventListener("popstate", this.handlePopState);
        }

        handlePopState() {
            this.syncFromUrl();
        }

        syncFromUrl() {
            const params = new URLSearchParams(window.location.search);
            const location = params.get("location") || "all";
            const view = params.get(VIEW_QUERY_PARAM) || "list";

            this.currentLocation = this.isValidLocation(location) ? location : "all";
            this.currentView = VALID_VIEWS.includes(view) ? view : "list";

            const filterButton = Array.from(this.filterButtons).find(
                (button) => button.dataset.location === this.currentLocation
            );

            if (filterButton) {
                this.setActiveFilterButton(filterButton);
            }

            const viewButton = Array.from(this.viewButtons).find(
                (button) => button.dataset.view === this.currentView
            );

            if (viewButton) {
                this.setActiveViewButton(viewButton);
            }

            this.setView(this.currentView, { skipUrlUpdate: true });

            if (!this.isValidLocation(location) || !VALID_VIEWS.includes(view)) {
                this.updateUrlParams();
            }
        }

        isValidLocation(location) {
            if (location === "all") {
                return true;
            }

            return Array.from(this.filterButtons).some(
                (button) => button.dataset.location === location
            );
        }

        updateUrlParams() {
            const params = new URLSearchParams(window.location.search);

            params.delete("view");

            if (this.currentLocation === "all") {
                params.delete("location");
            } else {
                params.set("location", this.currentLocation);
            }

            if (this.currentView === "list") {
                params.delete(VIEW_QUERY_PARAM);
            } else {
                params.set(VIEW_QUERY_PARAM, this.currentView);
            }

            const query = params.toString();
            const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
            const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

            if (nextUrl !== currentUrl) {
                window.history.replaceState({}, "", nextUrl);
            }
        }

        parseEventsData() {
            return this.parseJsonData("[data-event-details-events]");
        }

        parseEnelioCalendarData() {
            return this.parseJsonData("[data-event-details-enelio-calendar]");
        }

        parseJsonData(selector) {
            const dataScript = this.querySelector(selector);

            if (!dataScript) {
                return [];
            }

            try {
                return JSON.parse(dataScript.textContent);
            } catch (error) {
                window.console.error(`Unable to parse event details data for ${selector}`, error);
                return [];
            }
        }

        handleFilterClick(event) {
            const button = event.currentTarget;
            this.currentLocation = button.dataset.location;

            this.setActiveFilterButton(button);
            this.applyFilter(this.currentLocation);
            this.updateUrlParams();
        }

        handleViewClick(event) {
            const button = event.currentTarget;
            this.setView(button.dataset.view);
            this.setActiveViewButton(button);
        }

        setActiveFilterButton(activeButton) {
            this.filterButtons.forEach((button) => {
                const isActive = button === activeButton;

                button.classList.toggle("is-active", isActive);
                button.setAttribute("aria-pressed", isActive ? "true" : "false");
            });
        }

        setActiveViewButton(activeButton) {
            this.viewButtons.forEach((button) => {
                const isActive = button === activeButton;

                button.classList.toggle("is-active", isActive);
                button.setAttribute("aria-pressed", isActive ? "true" : "false");
            });
        }

        setView(view, options = {}) {
            this.currentView = view;

            if (this.listView) {
                this.listView.hidden = view !== "list";
            }

            if (this.calendarView) {
                if (view === "calendar") {
                    this.calendarView.hidden = false;
                    this.calendarView.removeAttribute("hidden");
                } else {
                    this.calendarView.hidden = true;
                }
            }

            if (view !== "calendar") {
                this.closeEventCalendarPopover();
            }

            if (view === "calendar") {
                this.ensureCalendarRendered();
            }

            this.applyFilter(this.currentLocation);
            this.updateCalendarEmptyState();

            if (!options.skipUrlUpdate) {
                this.updateUrlParams();
            }
        }

        applyFilter(location) {
            if (this.currentView === "list") {
                this.applyListFilter(location);
                return;
            }

            this.applyCalendarFilter(location);
        }

        applyListFilter(location) {
            this.listEventItems.forEach((item) => {
                const isVisible = this.matchesFilter(item, location);

                item.classList.toggle("is-filtered-hidden", !isVisible);
                item.toggleAttribute("hidden", !isVisible);
            });

            this.updateMonthGroups(this.listMonthGroups, location, {
                itemSelector: ".event-details-section__content-item:not(.is-filtered-hidden)",
                gapMonthSelector: ".event-details-section__month-group--empty",
            });
        }

        applyCalendarFilter(location) {
            const eventCalendar = this.getEventCalendar();

            if (!eventCalendar) {
                return;
            }

            this.closeEventCalendarPopover();

            const calendarEvents = this.querySelectorAll(
                ".event-details-calendar__event"
            );

            calendarEvents.forEach((item) => {
                const isVisible = this.matchesFilter(item, location);

                item.classList.toggle("is-filtered-hidden", !isVisible);
                item.toggleAttribute("hidden", !isVisible);
            });

            this.updateMonthGroups(eventCalendar.monthGroups, location, {
                itemSelector: ".event-details-calendar__event:not(.is-filtered-hidden)",
                gapMonthSelector: ".event-details-section__month-group--empty",
                uniqueByEventKey: true,
            });

            eventCalendar.updateFilteredDayStates?.();
            this.updateCalendarEmptyState();
        }

        matchesFilter(item, location) {
            if (location === "all") {
                return true;
            }

            if (item.dataset.allLocations === "true") {
                return true;
            }

            if (location === "virtual") {
                return item.dataset.isVirtual === "true";
            }

            return item.dataset.location === location;
        }

        updateMonthGroups(monthGroups, location, options) {
            monthGroups.forEach((group) => {
                const isGapMonth = group.matches(options.gapMonthSelector);

                if (isGapMonth) {
                    const hideGapMonth = location !== "all";

                    group.classList.toggle("is-filtered-hidden", hideGapMonth);
                    group.toggleAttribute("hidden", hideGapMonth);
                    return;
                }

                const visibleItems = group.querySelectorAll(options.itemSelector);
                const count = options.uniqueByEventKey
                    ? new Set(
                        Array.from(visibleItems).map(
                            (item) => item.dataset.eventKey || item.href
                        )
                    ).size
                    : visibleItems.length;
                const countElement = group.querySelector(
                    ".event-details-section__month-count"
                );

                if (countElement) {
                    countElement.textContent =
                        count === 1 ? "1 Event" : `${count} Events`;
                }

                const hideGroup = count === 0;

                group.classList.toggle("is-filtered-hidden", hideGroup);
                group.toggleAttribute("hidden", hideGroup);
            });
        }

        updateCalendarEmptyState() {
            if (!this.calendarMount || this.currentView !== "calendar" || !this.calendarRendered) {
                return;
            }

            const eventCalendar = this.getEventCalendar();

            if (!eventCalendar) {
                return;
            }

            const hasVisibleMonth = eventCalendar.monthGroups.some(
                (group) =>
                    !group.hidden && !group.classList.contains("is-filtered-hidden")
            );
            let emptyState = this.calendarMount.querySelector(
                ".event-details-section__calendar-empty"
            );

            if (!hasVisibleMonth) {
                if (!emptyState) {
                    emptyState = document.createElement("div");
                    emptyState.className =
                        "event-details-section__empty event-details-section__calendar-empty";
                    emptyState.textContent =
                        "No events for this location. Try another filter or check back soon.";
                    this.calendarMount.appendChild(emptyState);
                }

                return;
            }

            emptyState?.remove();
        }
    }
);
